import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@mui/material";
import NavigationBar from "../../../components/element/NavigationBar";
import FBOHeaderBar from "../../../components/element/FBOHeaderBar";
import { useQuery } from "@tanstack/react-query";
import useFBOApi from "../../../services/api/useFBOApi";
import ROUTE_PATHS from "../../../utils/routePaths";
import ViewLandingFeeInvoiceTable from "./Tables/ViewLandingFeeInvoiceTable";
import ViewFuelRoyaltyInvoiceTable from "./Tables/ViewFuelRoyaltyInvoiceTable";
import ViewLandLeaseInvoiceTable from "./Tables/ViewLandLeaseInvoiceTable";
import { formatToTwoDecimal } from "../../../utils/utils";
import { FBO_TYPE_URL_PARAMS, AIRCRAFT_TABS } from "../../../utils/constants";
import "./ViewInvoice.css";

const ViewInvoice = () => {
  const { aboType, invoiceId } = useParams();
  const [grandTotal, setGrandTotal] = useState(0);
  const [activeTab, setActiveTab] = useState(AIRCRAFT_TABS.PRIVATE);
  const {
    fetchLandingFeeInvoice,
    fetchFuelRoyaltyInvoice,
    fetchLandLeaseInvoice,
  } = useFBOApi();

  const {
    data: invoiceData,
    error,
    isLoading,
  } = useQuery({
    queryKey: [
      "view-fbo-invoice",
      invoiceId,
      aboType === FBO_TYPE_URL_PARAMS.LANDING_FEE ? activeTab : null,
    ],
    queryFn: () => {
      if (aboType === FBO_TYPE_URL_PARAMS.FUEL_ROYALTY) {
        return fetchFuelRoyaltyInvoice(invoiceId);
      } else if (aboType === FBO_TYPE_URL_PARAMS.AIRPORT_BASED_OPERATORS) {
        return fetchLandLeaseInvoice(invoiceId);
      } else {
        const isCommercialFlight = activeTab === AIRCRAFT_TABS.COMMERCIAL;
        return fetchLandingFeeInvoice(invoiceId, isCommercialFlight);
      }
    },
    retry: false,
  });

  useEffect(() => {
    const total =
      invoiceData?.data?.reduce((total, item) => {
        const value =
          aboType === FBO_TYPE_URL_PARAMS.LANDING_FEE
            ? item?.landingFeeCollected
            : aboType === FBO_TYPE_URL_PARAMS.FUEL_ROYALTY
            ? item?.totalFee + item?.tax
            : item?.totalFee + item?.tax;
        return total + (value || 0);
      }, 0) || 0;
    setGrandTotal(total);
  }, [invoiceData, aboType]);

  return (
    <div>
      <NavigationBar />
      <FBOHeaderBar
        title={"Invoice"}
        backContent={
          <div className="navigation-header">
            <Link to={ROUTE_PATHS.FBO_HOME} className="navigation-header-link">
              Home/
            </Link>
            <Link
              to={
                aboType === FBO_TYPE_URL_PARAMS.LANDING_FEE
                  ? ROUTE_PATHS.LANDING_FEE_HOME
                  : aboType === FBO_TYPE_URL_PARAMS.FUEL_ROYALTY
                  ? ROUTE_PATHS.FUEL_ROYALTY_HOME
                  : ROUTE_PATHS.AIRPORT_BASED_OPERATORS_HOME
              }
              className="navigation-header-link"
            >
              {aboType === FBO_TYPE_URL_PARAMS.LANDING_FEE
                ? "Landing Fee/"
                : aboType === FBO_TYPE_URL_PARAMS.FUEL_ROYALTY
                ? "Fuel Royalty/"
                : "Airport Based Operators - ABOs/"}
            </Link>
            <Link
              to={ROUTE_PATHS.INVOICES.replace(":aboType", aboType)}
              className="navigation-header-link"
            >
              Invoices/
            </Link>
            Invoice
          </div>
        }
      />
      <div className="view-invoice-top-row">
        {aboType === FBO_TYPE_URL_PARAMS.LANDING_FEE ? (
          <div className="view-invoice-tabs">
            <Button
              variant="text"
              className={`view-invoice-tab ${
                activeTab === AIRCRAFT_TABS.PRIVATE ? "active" : ""
              }`}
              onClick={() => setActiveTab(AIRCRAFT_TABS.PRIVATE)}
            >
              Private Aircraft
            </Button>
            <Button
              variant="text"
              className={`view-invoice-tab ${
                activeTab === AIRCRAFT_TABS.COMMERCIAL ? "active" : ""
              }`}
              onClick={() => setActiveTab(AIRCRAFT_TABS.COMMERCIAL)}
            >
              Commercial Aircraft
            </Button>
          </div>
        ) : (
          <div />
        )}
        <div className="view-invoice-grand-total-title">
          {`Grand Total: BSD ${formatToTwoDecimal(grandTotal)}`}
        </div>
      </div>

      {aboType === FBO_TYPE_URL_PARAMS.FUEL_ROYALTY ? (
        <ViewFuelRoyaltyInvoiceTable
          invoiceData={invoiceData?.data}
          isLoading={isLoading}
          error={error}
        />
      ) : aboType === FBO_TYPE_URL_PARAMS.AIRPORT_BASED_OPERATORS ? (
        <ViewLandLeaseInvoiceTable
          invoiceData={invoiceData?.data}
          isLoading={isLoading}
          error={error}
        />
      ) : (
        <div>
          <ViewLandingFeeInvoiceTable
            invoiceData={invoiceData?.data}
            isLoading={isLoading}
            error={error}
            isCommercialFlight={activeTab === AIRCRAFT_TABS.COMMERCIAL}
          />
        </div>
      )}
    </div>
  );
};

export default ViewInvoice;
